import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import { DateTime } from 'luxon'
import AuthService from 'services/auth'
import CashFlow from 'services/CashFlow'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'
import MoneyInput from 'components/Inputs/MoneyInput'
import DateInput from 'components/Inputs/DateInput'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'

import {
  Form,
  FormField,
  Input,
  Button,
  Row,
  Label,
  CheckboxLabel,
  InfoText,
  ErrorContainer
} from './styles'
import CheckBox from 'components/CheckBox'

const accountOption = account => ({
  label: account.bankName ? `${account.bankName} - ${account.description}` : account.description,
  value: account.bankAccountId
})

export default function BankBalanceModal({
  visible,
  balance = null,
  accounts = [],
  defaultAccountId = null,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const businessId = AuthService.getBusinessId()
  const requestAuth = AuthService.getRequestAuth()
  const [account, setAccount] = useState(null)
  const [date, setDate] = useState(null)
  const [value, setValue] = useState(0)
  const [negative, setNegative] = useState(false)
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  const options = accounts.map(accountOption)

  useEffect(() => {
    if (!visible) return

    const accountId = balance?.bank_account?.id ?? defaultAccountId

    setErrorMessage(null)
    setAccount(options.find(option => option.value === accountId) ?? null)
    setDate(balance?.date ?? DateTime.now().toISODate())
    setValue(Math.abs(balance?.value ?? 0))
    setNegative((balance?.value ?? 0) < 0)
    setDescription(balance?.description ?? '')
  }, [visible, balance])

  const submit = async event => {
    event?.preventDefault()

    if (!account) return setErrorMessage('Escolha uma conta')
    if (!date) return setErrorMessage('Informe a data do saldo')

    try {
      setSubmitting(true)
      setErrorMessage(null)

      const saved = await CashFlow.saveBalance({
        businessId,
        balance,
        requestAuth,
        payload: {
          bankAccountId: account.value,
          date,
          value: CashFlow.signedBalance(value, negative),
          description
        }
      })

      handleConfirm(saved)
      handleClose()
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{balance ? 'Editar saldo' : 'Lançar saldo'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form id='bank-balance-form' onSubmit={submit}>
            <Row>
              <FormField>
                <Label>Conta</Label>
                <Select
                  name='account'
                  placeholder='Conta...'
                  value={account}
                  options={options}
                  onChange={(_, option) => setAccount(option)}
                />
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Label>Data do saldo</Label>
                <DateInput value={date} onChange={setDate}/>
              </FormField>
              <FormField>
                <Label>Saldo</Label>
                <MoneyInput
                  name='value'
                  placeholder='Saldo'
                  value={value}
                  onValueChange={(_, newValue) => setValue(newValue)}
                />
              </FormField>
            </Row>
            <Row>
              <FormField>
                <CheckboxLabel style={{ marginTop: 0 }}>
                  <CheckBox value={negative} onValueChange={setNegative}/>
                  Saldo negativo
                </CheckboxLabel>
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Input
                  placeholder='Observação (opcional)'
                  value={description}
                  onChange={event => setDescription(event.target.value)}
                />
              </FormField>
            </Row>
            {errorMessage && (
              <ErrorContainer>
                <InfoText>{errorMessage}</InfoText>
              </ErrorContainer>
            )}
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button type='submit' form='bank-balance-form' disabled={submitting}>
            {submitting ? <ButtonLoading/> : balance ? 'Salvar saldo' : 'Lançar saldo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
